// eslint-disable-next-line boundaries/element-types
import { GeckoFileCategory } from 'types'
import { z } from 'zod'

export const checkFiles = (files: File[]) => {
  const hasOnlyImages = files.every((file: File) =>
    file.type.startsWith('image/')
  )
  const hasOnlyPDFs = files.every(
    (file: File) => file.type === 'application/pdf'
  )
  const hasOnlyOnePDF =
    files.every((file: File) => file.type === 'application/pdf') &&
    files.filter((file: File) => file.type === 'application/pdf').length === 1

  const hasOnlyDocOrDocxOrPDF = files.every(
    (file: File) =>
      file.type === 'application/pdf' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/msword'
  )

  const hasNoImages = !files.some((file: File) =>
    file.type.startsWith('image/')
  )

  return {
    hasOnlyImages,
    hasOnlyPDFs,
    hasOnlyOnePDF,
    hasOnlyDocOrDocxOrPDF,
    hasNoImages,
  }
}

export const FileSchema = z
  .object({
    fileCategory: z.enum(
      Object.values(GeckoFileCategory) as [string, ...string[]]
    ),
    components: z.array(z.string()).optional(),
    inspections: z.array(z.string()).optional(),
    files: z.array(z.instanceof(File)),
  })
  .superRefine((data, ctx) => {
    if (data.files.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['files'],
        message: 'At least one file must be uploaded',
      })
    }

    const {
      hasNoImages,
      hasOnlyDocOrDocxOrPDF,
      hasOnlyImages,
      hasOnlyOnePDF,
      hasOnlyPDFs,
    } = checkFiles(data.files)

    const hasNoInspections = !data.inspections || data.inspections.length === 0

    if (
      data.fileCategory === GeckoFileCategory.CONSTRUCTION_DRAWING ||
      data.fileCategory === GeckoFileCategory.INSPECTION_WALKDOWN_REPORT
    ) {
      if (!hasOnlyPDFs) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fileCategory'],
          message: 'Only PDFs are allowed for this category',
        })
      }
    }

    if (data.fileCategory === GeckoFileCategory.NON_GECKO_REPORT) {
      if (!hasOnlyDocOrDocxOrPDF) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fileCategory'],
          message: 'Only PDFs, .doc, and .docx are allowed for this category',
        })
      }
    }

    if (data.fileCategory === GeckoFileCategory.GECKO_REPORT) {
      if (!hasOnlyOnePDF) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fileCategory'],
          message: 'Only one PDF is allowed for this category',
        })
      }
    }

    if (data.fileCategory === GeckoFileCategory.GECKO_REPORT) {
      if (hasNoInspections) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['inspections'],
          message: 'Inspection selection required for Gecko Report',
        })
      }
    }

    if (data.fileCategory === GeckoFileCategory.IMAGE) {
      if (!hasOnlyImages) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fileCategory'],
          message: 'Only images are allowed for this category',
        })
      }
    }

    if (data.fileCategory === GeckoFileCategory.OTHER) {
      if (!hasNoImages) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fileCategory'],
          message: 'No images are allowed for this category',
        })
      }
    }
  })

export type FileSchema = z.infer<typeof FileSchema>
